import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Coins, Loader2, Lock, Check, Gift, Package, CalendarClock } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { FeatureLocked } from "@/components/FeatureLocked";
import { StatusBadge } from "@/components/Badges";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1607083206968-13611e3d76db?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

export default function Catalog() {
  const { user, refresh } = useAuth();
  const [view, setView] = useState<"catalog" | "redemptions">("catalog");
  const [rewards, setRewards] = useState<any[]>([]);
  const [filter, setFilter] = useState("Todas");
  const [selected, setSelected] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);

  const [reds, setReds] = useState<any[]>([]);
  const [redsLoading, setRedsLoading] = useState(false);

  // Un canje cada 6 meses (desde el último pedido no rechazado): se avisa antes de intentar.
  const [redeemStatus, setRedeemStatus] = useState<any>(null);
  const loadStatus = () => api.get("/customer/redeem-status").then((r) => setRedeemStatus(r.data)).catch(() => {});

  const load = () => api.get("/rewards").then((r) => setRewards(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); loadStatus(); }, []);
  const waiting = redeemStatus && !redeemStatus.can_redeem;

  const loadReds = () => {
    setRedsLoading(true);
    api.get("/customer/redemptions").then((r) => setReds(r.data)).finally(() => setRedsLoading(false));
  };
  useEffect(() => { if (view === "redemptions") loadReds(); }, [view]);

  const categories = ["Todas", ...Array.from(new Set(rewards.map((r) => r.category)))];
  const filtered = filter === "Todas" ? rewards : rewards.filter((r) => r.category === filter);

  const redeem = async () => {
    setRedeeming(true);
    try {
      await api.post("/customer/redeem", { reward_id: selected.id });
      toast.success("¡Canje solicitado! Está en revisión.");
      setSelected(null);
      await refresh();
      load();
      loadStatus();
    } catch (err: any) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      loadStatus();
    } finally {
      setRedeeming(false);
    }
  };

  const ctx = useOutletContext<any>();
  const feat = ctx?.features?.catalog;
  const redFeat = ctx?.features?.redemptions;
  const showRedemptionsTab = !redFeat || redFeat.available;
  if (feat && !feat.available) return <FeatureLocked title="Catálogo de Recompensas" unlockTier={feat.unlock_tier} enabled={feat.enabled} />;

  if (loading)
    return <div className="flex justify-center h-96 items-center"><Loader2 className="w-7 h-7 text-gold animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="catalog-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-medium">Catálogo de Recompensas</h1>
          <p className="text-sm text-slate-500 mt-1">Canjea tus puntos por experiencias y productos exclusivos.</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20">
          <Coins className="w-4 h-4 text-gold" />
          <span className="text-gold-ink font-mono font-semibold">{user?.points?.toLocaleString()} pts</span>
        </div>
      </div>

      {/* View tabs */}
      <div className="inline-flex items-center gap-1 p-1 rounded-full bg-slate-100 border border-slate-200">
        <button
          data-testid="catalog-tab-rewards"
          onClick={() => setView("catalog")}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
            view === "catalog" ? "gold-gradient-bg text-white font-semibold" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <Gift className="w-4 h-4" /> Recompensas
        </button>
        {showRedemptionsTab && (
          <button
            data-testid="catalog-tab-redemptions"
            onClick={() => setView("redemptions")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
              view === "redemptions" ? "gold-gradient-bg text-white font-semibold" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Package className="w-4 h-4" /> Mis canjes
          </button>
        )}
      </div>

      {waiting && (
        <div data-testid="redeem-cooldown-banner" role="status" className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/40 text-sm text-amber-900 flex items-start gap-3">
          <CalendarClock className="w-5 h-5 shrink-0 mt-0.5" aria-hidden="true" />
          <p>
            Puedes hacer un canje cada {redeemStatus.cooldown_months} meses. Tu último canje fue el <strong>{redeemStatus.last_redemption_label}</strong>,
            así que podrás canjear de nuevo a partir del <strong>{redeemStatus.next_redeem_label}</strong>. Mientras tanto sigues sumando puntos,
            y canjear no te baja de nivel.
          </p>
        </div>
      )}

      {view === "catalog" && (
        <>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setFilter(c)}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                  filter === c ? "gold-gradient-bg text-white font-semibold border-transparent" : "border-slate-200 text-slate-500 hover:text-slate-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((r) => {
              const affordable = (user?.points || 0) >= r.points_required && r.stock > 0 && !waiting;
              return (
                <div key={r.id} data-testid="reward-card" className="card-obsidian overflow-hidden flex flex-col group">
                  <div className="relative h-44 overflow-hidden">
                    <img src={r.image || FALLBACK_IMG} alt={r.name} onError={(e: any) => (e.currentTarget.src = FALLBACK_IMG)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                    <span className="absolute top-3 left-3 text-xs px-2.5 py-1 rounded-full crystal-glass text-slate-800">{r.category}</span>
                    {r.stock <= 0 && (
                      <span className="absolute top-3 right-3 text-xs px-2.5 py-1 rounded-full bg-red-500/80 text-white">Agotado</span>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-lg font-semibold text-slate-900">{r.name}</h3>
                    <p className="text-sm text-slate-500 mt-1.5 line-clamp-2 flex-1">{r.description}</p>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-gold-ink font-mono font-semibold flex items-center gap-1">
                        <Coins className="w-4 h-4" /> {r.points_required.toLocaleString()}
                      </span>
                      <Button
                        data-testid="reward-card-redeem-button"
                        onClick={() => setSelected(r)}
                        disabled={!affordable}
                        className={affordable ? "gold-gradient-bg text-white font-semibold rounded-full px-5" : "bg-slate-100 text-slate-500 rounded-full px-5"}
                      >
                        {affordable ? "Canjear" : <><Lock className="w-3.5 h-3.5 mr-1" /> {r.stock <= 0 ? "Agotado" : waiting ? `Desde ${new Date(redeemStatus.next_redeem_at).toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric", timeZone: "America/Guayaquil" })}` : "Faltan pts"}</>}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {view === "redemptions" && (
        <div data-testid="redemptions-page">
          {redsLoading ? (
            <div className="flex justify-center h-64 items-center"><Loader2 className="w-7 h-7 text-gold animate-spin" /></div>
          ) : reds.length === 0 ? (
            <div className="card-obsidian p-12 text-center">
              <Package className="w-10 h-10 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500">Aún no has realizado ningún canje.</p>
              <Button onClick={() => setView("catalog")} className="mt-5 gold-gradient-bg text-white font-semibold rounded-full">
                Ver recompensas
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {reds.map((r) => (
                <div key={r.id} data-testid="redemption-item" className="card-obsidian p-4 flex items-center gap-4">
                  <img src={r.reward_image} alt={r.reward_name} className="w-16 h-16 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{r.reward_name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {new Date(r.created_at).toLocaleDateString("es", { day: "numeric", month: "long", year: "numeric", timeZone: "America/Guayaquil" })}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-gold-ink font-mono text-sm font-semibold mb-1.5">-{r.points_spent.toLocaleString()} pts</p>
                    <StatusBadge status={r.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
          {selected && (
            <>
              <img src={selected.image || FALLBACK_IMG} alt={selected.name} onError={(e: any) => (e.currentTarget.src = FALLBACK_IMG)} className="w-full h-48 object-cover rounded-lg" />
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{selected.name}</DialogTitle>
                <DialogDescription className="text-slate-500">{selected.description}</DialogDescription>
              </DialogHeader>
              <div className="flex items-center justify-between py-3 border-y border-slate-200">
                <span className="text-sm text-slate-500">Costo del canje</span>
                <span className="text-gold-ink font-mono font-semibold text-lg">{selected.points_required.toLocaleString()} pts</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Tu saldo tras el canje</span>
                <span className="text-slate-800 font-mono">{((user?.points || 0) - selected.points_required).toLocaleString()} pts</span>
              </div>
              <p className="text-xs text-slate-600">
                Tu nivel no baja al canjear. Después de este canje podrás hacer el siguiente en {redeemStatus?.cooldown_months ?? 6} meses.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)} className="border-slate-200 text-slate-700 rounded-full">
                  Cancelar
                </Button>
                <Button
                  data-testid="confirm-redeem-button"
                  onClick={redeem}
                  disabled={redeeming}
                  className="gold-gradient-bg text-white font-semibold rounded-full"
                >
                  {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Confirmar canje</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
